'use client'

import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Check, Copy, Loader, QrCode } from 'lucide-react'
import { motion } from 'motion/react'

import { Button, Skeleton } from '@ui/web/components'
import { buildUrl } from '@helper/utils/format'
import { useHandleCopy } from '@helper/web/hooks'
import cn from '@config/tailwind/cn'

import { useSharedTRPC } from '../../context'

const footerSectionClassName =
  'srd:w-full srd:px-4 srd:py-2 srd:rounded-lg srd:border srd:border-neutrals-n-20 srd:flex srd:flex-col srd:gap-y-4'

interface SharePinDialogFooterLoadingProps {
  className?: string
}

export function SharePinDialogFooterLoading({ className }: SharePinDialogFooterLoadingProps) {
  return (
    <section className={cn(footerSectionClassName, className)}>
      <section className='srd:flex srd:flex-col srd:gap-y-2 srd:w-full'>
        <p className='srd:w-full srd:text-center srd:text-xs srd:text-neutrals-n-75 srd:font-medium'>
          Atau gunakan tautan khusus dengan kode aksesmu:
        </p>

        <div className='srd:w-full srd:flex srd:items-center srd:justify-center srd:gap-x-2 srd:px-2 srd:py-1 srd:border srd:border-neutrals-n-35 srd:rounded-md srd:flex-nowrap'>
          <Skeleton className='srd:flex-1 srd:h-4 srd:rounded' />
          <Skeleton className='srd:size-4 srd:rounded' />
        </div>
      </section>

      <div className='srd:w-full srd:flex srd:items-center srd:justify-center'>
        <Button variant='link' size='sm' disabled className='srd:w-fit srd:h-fit srd:p-0'>
          <Loader className='srd:size-4 srd:mr-2 srd:animate-spin srd:text-main-blue-100' />
          <QrCode className='srd:size-4 srd:mr-2 srd:text-main-blue-100' />
          Bagikan kode QR
        </Button>
      </div>
    </section>
  )
}

interface SharePinDialogFooterErrorProps {
  className?: string
  refetch: () => void
}

export function SharePinDialogFooterError({ className, refetch }: SharePinDialogFooterErrorProps) {
  return (
    <section className={cn(footerSectionClassName, className)}>
      <section className='srd:flex srd:flex-col srd:gap-y-2 srd:w-full'>
        <p className='srd:w-full srd:text-center srd:text-xs srd:text-neutrals-n-75 srd:font-medium'>
          Atau gunakan tautan khusus dengan kode aksesmu:
        </p>

        <div className='srd:w-full srd:flex srd:items-center srd:justify-center srd:gap-x-2 srd:px-2 srd:py-1 srd:border srd:border-neutrals-n-35 srd:rounded-md srd:flex-nowrap'>
          <p className='srd:w-full srd:flex-1 srd:text-sm srd:font-normal srd:text-main-red-100'>Gagal memuat tautan</p>
          <Button variant='outline' size='sm' className='srd:text-xs srd:h-6' onClick={() => refetch()}>
            Coba lagi
          </Button>
        </div>
      </section>
    </section>
  )
}

interface SharePinDialogFooterProps {
  orgId: string
  className?: string
  type: 'school' | 'web'
}

export function SharePinDialogFooter({ orgId, className, type }: SharePinDialogFooterProps) {
  const trpc = useSharedTRPC()

  const { copy, handleCopy } = useHandleCopy()

  const { data: getPdfData } = useQuery({
    ...trpc.myOrganization.getLoginPoster.queryOptions({ type }),
    staleTime: Infinity,
  })

  const handleDownload = React.useCallback(() => {
    if (!getPdfData) return

    // Convert base64 to blob and download
    const byteCharacters = atob(getPdfData.pdf)
    const byteArray = new Uint8Array(byteCharacters.length)
    for (let i = 0; i < byteCharacters.length; i++) {
      byteArray[i] = byteCharacters.charCodeAt(i)
    }
    const blob = new Blob([byteArray], { type: 'application/pdf' })

    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'PiBo_Panduan_Login.pdf'
    a.click()
    URL.revokeObjectURL(url)
  }, [getPdfData])

  return (
    <section className={cn(footerSectionClassName, className)}>
      <section className='srd:flex srd:flex-col srd:gap-y-2 srd:w-full'>
        <p className='srd:w-full srd:text-center srd:text-xs srd:text-neutrals-n-75 srd:font-medium'>
          Atau gunakan tautan khusus dengan kode aksesmu:
        </p>

        <div className='srd:w-full srd:flex srd:items-center srd:justify-center srd:gap-x-2 srd:px-2 srd:py-1 srd:border srd:border-neutrals-n-35 srd:rounded-md srd:flex-nowrap'>
          <p className='srd:w-full srd:flex-1 srd:text-neutrals-black srd:text-sm srd:font-normal srd:truncate srd:line-clamp-1'>
            {buildUrl('account', `/anak/${orgId}`).href}
          </p>

          <Button
            variant='link'
            className='srd:p-0 srd:h-fit srd:w-fit'
            onClick={() => handleCopy(buildUrl('account', `/anak/${orgId}`).href)}
            disabled={copy}
          >
            {copy ? <Check className='srd:size-4' /> : <Copy className='srd:size-4' />}
          </Button>
        </div>
      </section>

      <div className='srd:w-full srd:flex srd:items-center srd:justify-center'>
        {(() => {
          if (!getPdfData?.pdf)
            return (
              <Button variant='link' size='sm' disabled className='srd:w-fit srd:h-fit srd:p-0'>
                <Loader className='srd:size-4 srd:mr-2 srd:animate-spin srd:text-main-blue-100' />
                <QrCode className='srd:size-4 srd:mr-2 srd:text-main-blue-100' />
                Bagikan kode QR
              </Button>
            )

          return (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className='srd:w-fit srd:h-fit srd:text-main-blue-100 srd:flex srd:items-center srd:text-sm srd:font-medium srd:disabled:opacity-50 srd:disabled:cursor-not-allowed'
              type='button'
              onClick={() => handleDownload()}
            >
              <QrCode className='srd:w-4 srd:h-4 srd:mr-2 srd:text-main-blue-100' />
              Bagikan kode QR
            </motion.button>
          )
        })()}
      </div>
    </section>
  )
}
